import { AddCircleOutline, Delete, DragIndicator, Launch, Visibility } from '@mui/icons-material';
import { Box, Button, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Select, Switch, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { getQuestions, updateQuestions } from '../../../../utils/mutations/form';
import { Conference, Question, generateUUID } from '../../../../utils/types';
import MultipleChoiceDialog from './MultipleChoiceDialog';
import ResponsesView from './ResponsesView';

interface FormTabProps {
    conference: Conference;
}

interface QuestionRowProps {
    question: Question;
    index: number;
    moveQuestion: (dragIndex: number, hoverIndex: number) => void;
    updateQuestion: (index: number, question: Question) => void;
    deleteQuestion: (index: number) => void;
}

const QuestionRow: React.FC<QuestionRowProps> = ({ question, index, moveQuestion, updateQuestion, deleteQuestion }) => {
    const [optionsDialogOpen, setOptionsDialogOpen] = useState(false);
    const [{ isDragging }, drag] = useDrag({
        type: 'QUESTION',
        item: { index },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const [, drop] = useDrop({
        accept: 'QUESTION',
        hover: (item: { index: number }) => {
            if (item.index === index) {
                return;
            }
            moveQuestion(item.index, index);
            item.index = index;
        },
    });

    const handleQuestionChange = (field: keyof Question, value: any) => {
        updateQuestion(index, { ...question, [field]: value });
    };

    const handlePropertyChange = (key: string, value: any) => {
        const properties = { ...question.properties, [key]: value };
        updateQuestion(index, { ...question, properties });
    };

    const handleAddOptions = (options: string[]) => {
        const currentOptions = question.properties?.options || [];
        handlePropertyChange('options', [...currentOptions, ...options]);
    };

    const handleRemoveOption = (optionIndex: number) => {
        const currentOptions = question.properties?.options || [];
        handlePropertyChange('options', currentOptions.filter((_: unknown, i: any) => i !== optionIndex));
    };

    return (
        <>
            <Box
                ref={(node) => drag(drop(node as HTMLElement | null))}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '75%',
                    gap: '20px',
                    p: '20px',
                    marginBottom: '10px',
                    opacity: isDragging ? 0.5 : 1,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    backgroundColor: isDragging ? '#f5f5f5' : 'white',
                }}
            >
                <DragIndicator sx={{ cursor: 'move' }} />
                <TextField
                    label="Question"
                    value={question.question}
                    onChange={(e) => handleQuestionChange('question', e.target.value)}
                    size="small"
                    sx={{ flexGrow: 1 }}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Format</InputLabel>
                    <Select
                        value={question.format}
                        label="Format"
                        onChange={(e) => handleQuestionChange('format', e.target.value)}
                    >
                        <MenuItem value="oneline">Single Line</MenuItem>
                        <MenuItem value="multiline">Multi Line</MenuItem>
                        <MenuItem value="date">Date</MenuItem>
                        <MenuItem value="number">Number</MenuItem>
                        <MenuItem value="email">Email</MenuItem>
                        <MenuItem value="multiplechoice">Multiple Choice</MenuItem>
                        <MenuItem value="hline">Horizontal Line</MenuItem>
                    </Select>
                </FormControl>
                {question.format === 'multiplechoice' && (
                    <IconButton
                        size="small"
                        onClick={() => setOptionsDialogOpen(true)}
                    >
                        <Launch />
                    </IconButton>
                )}
                <FormControlLabel
                    control={
                        <Switch
                            disabled={question.format === 'hline'}
                            checked={question.properties?.required || false}
                            onChange={(e) => handlePropertyChange('required', e.target.checked)}
                        />
                    }
                    label="Required"
                />
                <IconButton onClick={() => deleteQuestion(index)} color="error">
                    <Delete />
                </IconButton>
            </Box>

            <MultipleChoiceDialog
                open={optionsDialogOpen}
                options={question.properties?.options || []}
                onAddOptions={handleAddOptions}
                onRemoveOption={handleRemoveOption}
                onClose={() => setOptionsDialogOpen(false)}
            />
        </>
    );
};

const FormTab: React.FC<FormTabProps> = ({ conference }) => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [editing, setEditing] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);

    const resetQuestions = async () => {
        const data = await getQuestions(conference.conferenceCode);
        setQuestions(data);
    };

    useEffect(() => {
        resetQuestions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const moveQuestion = (dragIndex: number, hoverIndex: number) => {
        const dragQuestion = questions[dragIndex];
        const newQuestions = [...questions];
        newQuestions.splice(dragIndex, 1);
        newQuestions.splice(hoverIndex, 0, dragQuestion);

        setQuestions(newQuestions);
    };

    const updateQuestion = (index: number, question: Question) => {
        const newQuestions = [...questions];
        newQuestions[index] = question;
        setQuestions(newQuestions);
    };

    const deleteQuestion = (index: number) => {
        const newQuestions = questions.filter((_, i) => i !== index);
        setQuestions(newQuestions);
    };

    const addQuestion = () => {
        const newQuestion: Question = {
            id: generateUUID(),
            index: questions.length,
            question: '',
            format: 'oneline',
            properties: {
                required: false
            }
        };
        setQuestions([...questions, newQuestion]);
    };

    const handleSave = async () => {
        const invalidQuestions = questions.filter((q) => !q.question.trim() && q.format !== 'hline');
        if (invalidQuestions.length > 0) {
            return alert("All questions must have a valid question text.");
        }
    
        const reordered = questions.map((q, index) => ({ ...q, index }));
    
        const password = prompt("Please enter your password to confirm changes:");
        if (password !== "admin123") return alert("Incorrect password!");
    
        try {
            await updateQuestions(conference.conferenceCode, reordered);
            setEditing(false);
        } catch (error: any) {
            console.error("Error saving changes:", error);
            alert(error.message || "Failed to save changes. Please try again.");
        }
    };
    

    const handleCancel = async () => {
        if (window.confirm("Are you sure you want to cancel? Unsaved changes will be lost.")) {
            setEditing(false);
            // Reset questions to the original state
            await resetQuestions();
        }
    };

    return (
        <Box sx={{  width: '100%',
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    padding: '20px', 
                    gap: '20px',
                }}>
            <Typography variant="h5">Questions</Typography>

            {editing ? (
            <Box sx={{
                width: '75%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '20px',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                backgroundColor: '#f9f9f9',
                maxHeight: '60vh',
                overflowY: 'scroll',
            }}>
                <DndProvider backend={HTML5Backend}>
                    {questions.map((question, index) => (
                        <QuestionRow
                            key={question.id}
                            question={question}
                            index={index}
                            moveQuestion={moveQuestion}
                            updateQuestion={updateQuestion}
                            deleteQuestion={deleteQuestion}
                        />
                    ))}
                    <Button
                        startIcon={<AddCircleOutline />}
                        onClick={addQuestion}
                    >
                        Add Question
                    </Button>
                </DndProvider>
                <Box sx={{
                    marginTop: '20px',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    gap: '10px'
                }}>
                <Button variant="outlined" onClick={handleSave} sx={{ mr: 1 }}>
                    Save
                </Button>
                <Button onClick={handleCancel}>
                    Cancel
                </Button>
            </Box>
            </Box>
            ) : (
            <>
            <Box sx={{
                width: '75%',
                gap: '20px',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                maxHeight: '60vh',
                overflowY: 'scroll',
            }}>
                {questions.length === 0 ? (
                    <Typography color="text.secondary">No questions added yet.</Typography>
                ) : (
                    questions.map((question) => (
                        <Box key={question.id} sx={{
                            p: '15px', 
                            border: '1px solid #e0e0e0', 
                            borderRadius: 1, 
                            width: '50%',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <Box>
                                <Typography variant="subtitle1">
                                    {question.question}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {question.format}
                                    {question.properties?.required && ' • required'}
                                </Typography>
                            </Box>
                            <IconButton 
                                onClick={() => setSelectedQuestion(question)}
                                color="primary"
                            >
                                <Visibility />
                            </IconButton>
                        </Box>
                    ))
                )}
            </Box>
            <Button variant="outlined" onClick={() => setEditing(true)}>
                Edit
            </Button>
            </>
            )}

            {selectedQuestion && (
                <ResponsesView
                    conferenceCode={conference.conferenceCode}
                    question={selectedQuestion}
                    onClose={() => setSelectedQuestion(null)}
                />
            )}
        </Box>
    );
};

export default FormTab;